import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useOrganization } from '@clerk/clerk-react';

const steps = [
  { id: 1, label: 'Copia el snippet', icon: '📋' },
  { id: 2, label: 'Instala en tu tienda', icon: '🏪' },
  { id: 3, label: 'Verifica la instalación', icon: '✅' },
];

export default function InstallPage() {
  const { organization } = useOrganization();
  const [snippet, setSnippet] = useState('');
  const [modules, setModules] = useState({ search: false, chat: false });
  const [planKey, setPlanKey] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [platform, setPlatform] = useState('prestashop');
  const [copied, setCopied] = useState(false);
  const [psModuleVersion, setPsModuleVersion] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    api.get('/api/snippet').then(r => {
      setSnippet(r.data.snippet);
      setModules(r.data.modules || { search: false, chat: false });
      setPlanKey(r.data.plan_key || null);
    }).catch(() => {});

    // Detect PrestaShop module version from the download endpoint header
    // (so the merchant sees "Versión actual: 2.2.0" before downloading).
    fetch(`${BACKEND_URL}/api/widget/prestashop-module`, { method: 'GET' })
      .then(r => {
        const v = r.headers.get('X-Sera-Module-Version');
        if (v) setPsModuleVersion(v);
        // Cancel the body download — we only wanted the header
        if (r.body && r.body.cancel) { try { r.body.cancel(); } catch (e) {} }
      })
      .catch(() => {});
  }, [BACKEND_URL]);

  const openDemo = () => {
    if (!organization) return;
    const demoUrl = `/widget-demo.html?orgId=${organization.id}`;
    window.open(demoUrl, '_blank');
  };

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPrestaShopModule = () => {
    window.open(`${BACKEND_URL}/api/widget/prestashop-module`, '_blank');
  };

  const ModulesBadge = () => {
    const noModules = !modules.search && !modules.chat;
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-4" data-testid="modules-badge">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Tu plan actual</div>
            <div className="text-white text-lg font-semibold">
              {planKey ? planKey.replace('_', ' ').toUpperCase() : 'Sin plan'}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <span
              data-testid="module-pill-search"
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                modules.search
                  ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300'
                  : 'bg-gray-800 border-gray-700 text-gray-500'
              }`}
            >
              {modules.search ? '✓' : '○'} Buscador
            </span>
            <span
              data-testid="module-pill-chat"
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                modules.chat
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-gray-800 border-gray-700 text-gray-500'
              }`}
            >
              {modules.chat ? '✓' : '○'} Chat IA
            </span>
          </div>
        </div>
        {noModules && (
          <div className="mt-3 text-xs text-amber-400">
            No tienes módulos activos. Contrata un plan en <a href="/billing" className="underline">Billing</a>.
          </div>
        )}
        {modules.search && modules.chat && (
          <div className="mt-3 text-xs text-gray-400">
            Tienes <strong className="text-white">ambos módulos</strong> contratados.
            El snippet de abajo cargará automáticamente el buscador y la burbuja de chat.
          </div>
        )}
        {modules.search && !modules.chat && (
          <div className="mt-3 text-xs text-gray-400">
            Solo tienes el <strong className="text-white">Buscador</strong>. Si quieres el chat,
            cambia a plan <em>Chat</em> o <em>Full</em> en <a href="/billing" className="underline">Billing</a>.
          </div>
        )}
        {!modules.search && modules.chat && (
          <div className="mt-3 text-xs text-gray-400">
            Solo tienes el <strong className="text-white">Chat IA</strong> (burbuja flotante).
            Para añadir buscador inteligente, cambia a plan <em>Search AI</em> o <em>Full</em>.
          </div>
        )}
      </div>
    );
  };

  const platformInstructions = {
    prestashop: [
      { step: 1, title: 'Accede al Back Office', desc: 'Ve a Diseño → Editor de Temas → selecciona tu tema activo' },
      { step: 2, title: 'Edita el header', desc: 'Busca el archivo header.tpl (o header.php según tu versión)' },
      { step: 3, title: 'Pega el snippet', desc: 'Añade el código justo antes de </head>' },
      { step: 4, title: 'Guarda y limpia caché', desc: 'Guarda el archivo y vacía la caché desde Parámetros Avanzados → Rendimiento' },
    ],
    shopify: [
      { step: 1, title: 'Accede al panel de Shopify', desc: 'Ve a Online Store → Themes → Edit code' },
      { step: 2, title: 'Edita theme.liquid', desc: 'Abre el archivo theme.liquid en Layout/' },
      { step: 3, title: 'Pega el snippet', desc: 'Añade el código justo antes de </head>' },
      { step: 4, title: 'Guarda', desc: 'Haz clic en Save y verifica en tu tienda' },
    ],
    woocommerce: [
      { step: 1, title: 'Accede a WordPress', desc: 'Ve a Apariencia → Editor de Temas (o usa un plugin de header)' },
      { step: 2, title: 'Edita header.php', desc: 'Abre el archivo header.php de tu tema hijo' },
      { step: 3, title: 'Pega el snippet', desc: 'Añade el código justo antes de </head>' },
      { step: 4, title: 'Guarda y limpia caché', desc: 'Guarda los cambios y vacía cualquier plugin de caché' },
    ],
    other: [
      { step: 1, title: 'Copia el snippet', desc: 'Copia el código generado arriba' },
      { step: 2, title: 'Encuentra tu header', desc: 'Localiza el archivo de cabecera de tu tienda' },
      { step: 3, title: 'Pega antes de </head>', desc: 'Inserta el snippet justo antes de la etiqueta de cierre </head>' },
      { step: 4, title: 'Publica los cambios', desc: 'Guarda y despliega los cambios en tu servidor' },
    ],
  };

  return (
    <div className="p-8 max-w-4xl">
      <h1 className="text-2xl font-bold text-white mb-2">Instalación del widget</h1>
      <p className="text-gray-400 mb-8">Instala Sera en tu tienda en 3 pasos. Compatible con cualquier plataforma.</p>

      {/* Progress steps */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((step, i) => (
          <React.Fragment key={step.id}>
            <button
              onClick={() => setActiveStep(step.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeStep === step.id
                  ? 'bg-indigo-600 text-white'
                  : activeStep > step.id
                  ? 'bg-emerald-600/20 text-emerald-400'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <span>{step.icon}</span>
              <span>{step.label}</span>
            </button>
            {i < steps.length - 1 && <span className="text-gray-700">→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Copy snippet */}
      {activeStep === 1 && (
        <div className="space-y-4">
          <ModulesBadge />

          {/* Organization ID — needed for PS/Shopify module config */}
          {organization?.id && (
            <div className="bg-gray-900 border border-indigo-800/50 rounded-xl p-5">
              <div className="text-xs uppercase tracking-wider text-indigo-400 mb-2 font-medium">Tu Organization ID</div>
              <div className="flex items-center gap-3">
                <code
                  data-testid="org-id-display"
                  className="flex-1 bg-gray-950 border border-gray-700 rounded-lg px-4 py-2.5 text-indigo-300 font-mono text-sm truncate"
                >
                  {organization.id}
                </code>
                <button
                  data-testid="copy-org-id-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(organization.id);
                  }}
                  className="shrink-0 bg-indigo-700 hover:bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Copiar
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Necesario para configurar el módulo de PrestaShop o Shopify. Pégalo en el campo "Organization ID" del módulo.
              </p>
            </div>
          )}

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">Tu snippet de instalación</h2>
              <button
                onClick={copySnippet}
                data-testid="copy-snippet-btn"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  copied ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {copied ? '✓ Copiado' : '📋 Copiar'}
              </button>
            </div>
            <pre data-testid="install-snippet" className="bg-gray-950 border border-gray-700 rounded-lg p-4 text-green-400 text-xs overflow-x-auto whitespace-pre-wrap">
              {snippet || 'Cargando snippet...'}
            </pre>
            <p className="text-gray-500 text-xs mt-3">
              Este snippet se adapta automáticamente a tu plan. Si añades o quitas módulos,
              el snippet se actualizará sin necesidad de cambiar nada en tu tienda.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setActiveStep(2)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
            >
              Continuar →
            </button>
            <button
              onClick={openDemo}
              className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              🚀 Probar demo en vivo
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Install */}
      {activeStep === 2 && (
        <div className="space-y-4">
          {/* Platform selector */}
          <div className="flex gap-2 flex-wrap">
            {['prestashop', 'shopify', 'woocommerce', 'other'].map(p => (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                  platform === p ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {p === 'prestashop' ? 'PrestaShop' : p === 'shopify' ? 'Shopify' : p === 'woocommerce' ? 'WooCommerce' : 'Otra plataforma'}
              </button>
            ))}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">
              Instrucciones para {platform === 'prestashop' ? 'PrestaShop' : platform === 'shopify' ? 'Shopify' : platform === 'woocommerce' ? 'WooCommerce' : 'tu plataforma'}
            </h2>

            {platform === 'prestashop' && (
              <div className="mb-5 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-indigo-200 text-sm font-medium">
                        Módulo oficial Sera para PrestaShop
                      </p>
                      {psModuleVersion && (
                        <span
                          data-testid="ps-module-version-badge"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/40 text-indigo-200 text-[11px] font-mono"
                          title="Versión publicada en este servidor"
                        >
                          v{psModuleVersion}
                        </span>
                      )}
                    </div>
                    <p className="text-indigo-300/80 text-xs leading-relaxed">
                      Recomendado. Instala el módulo y solo necesitas tu <code className="bg-black/40 px-1.5 py-0.5 rounded">Organization ID</code>.
                      Detecta automáticamente tu plan, gestiona el webservice y carga solo los scripts contratados.
                    </p>
                  </div>
                  <button
                    onClick={downloadPrestaShopModule}
                    data-testid="download-prestashop-module-btn"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                  >
                    ⬇ Descargar {psModuleVersion ? `v${psModuleVersion}` : 'ZIP'}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {platformInstructions[platform].map(inst => (
                <div key={inst.step} className="flex gap-4">
                  <div className="w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    {inst.step}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{inst.title}</p>
                    <p className="text-gray-400 text-sm">{inst.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setActiveStep(1)} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg text-sm">
              ← Atrás
            </button>
            <button onClick={() => setActiveStep(3)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium">
              Ya instalado →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Verify */}
      {activeStep === 3 && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Verificar instalación</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                <span className="text-emerald-400">✓</span>
                <span className="text-gray-300 text-sm">Abre tu tienda en una ventana nueva</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                <span className="text-emerald-400">✓</span>
                <span className="text-gray-300 text-sm">Haz clic en la barra de búsqueda de tu tienda</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                <span className="text-emerald-400">✓</span>
                <span className="text-gray-300 text-sm">El overlay de Sera debe abrirse a pantalla completa</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                <span className="text-emerald-400">✓</span>
                <span className="text-gray-300 text-sm">Prueba una búsqueda y verifica que aparecen resultados</span>
              </div>
            </div>
          </div>

          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5">
            <p className="text-emerald-300 font-medium">🎉 ¡Ya está!</p>
            <p className="text-gray-400 text-sm mt-1">
              Una vez instalado, las búsquedas aparecerán automáticamente en tu Dashboard.
              El widget es completamente configurable desde la sección Personalizar.
            </p>
          </div>

          <button onClick={() => setActiveStep(2)} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg text-sm">
            ← Atrás
          </button>
        </div>
      )}
    </div>
  );
}
